import React from "react";

import { parse, parseISO, add, format, isPast, isValid } from "date-fns";
import { CoverageStartDateAt } from "EmployerApplication/Components/FinalFormQuestions";
import { quotePageURL } from "QuotePage/urls";
import { useFormState } from "react-final-form";
import { deserializeDate } from "Redux/serializers";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { Callout } from "Cobalt/Callout";
import AppearingElement from "Common/AppearingElement";
import { Row } from "Common/Grid";
import { Div } from "Common/HTML";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import Button from "Theme/Button";

function goToQuotePage() {
  AnalyticsTracking.trackEvent("cta_clicked", {
    location: "owner_enrollment_page",
    action: "change_plan",
  });

  window.location.replace(appendAuthParams(quotePageURL()));
}

// Callout bodies below are built even while their AppearingElement is hidden, so every date
// formatted here has to tolerate a missing or unparseable value instead of throwing.
const formatDay = (date) => (isValid(date) ? format(date, "LLLL do") : "");

const CoverageStartDateSection = ({ companyApplication }) => {
  const coverageStartDateString = useFormState().values.company_application?.coverage_start_date_at;
  const selectedCoverageStartDate = parse(coverageStartDateString, "MM/dd/yyyy", new Date());

  const carrierDeadline = parse(companyApplication.carrierDeadlines[coverageStartDateString], "yyyy-MM-dd", new Date());

  const carrierDeadlineKnown = isValid(carrierDeadline);
  const carrierDeadlinePassed = carrierDeadlineKnown && isPast(add(carrierDeadline, { days: 1 }));
  const carrierName = companyApplication.planBrand;
  const alternativeCarriers = companyApplication.alternativeCarriersForCoverageStartDate[coverageStartDateString] || [];
  // Null once every allowed start date's deadline has passed.
  const nextCarrierDeadline = deserializeDate(companyApplication.nextCarrierDeadline);

  const policyStartDateOptions = companyApplication.allowedPolicyStartDates
    .map((dateString) => parseISO(dateString))
    .filter(isValid)
    .reduce((accum, date) => {
      const displayValue = format(date, "LLLL do");
      const value = format(date, "MM/dd/yyyy");
      accum[displayValue] = value;
      return accum;
    }, {});

  return (
    <Div className="min-h-[130px]">
      <Row>
        <CoverageStartDateAt policyStartDateOptions={policyStartDateOptions} md={8} />
      </Row>
      <Row>
        {coverageStartDateString && (
          <>
            <AppearingElement show={carrierDeadlinePassed && coverageStartDateString && nextCarrierDeadline}>
              <Callout
                className="mx-4"
                type="warning"
                body={
                  <>
                    <p>
                      {carrierName}'s deadline for {formatDay(selectedCoverageStartDate)} coverage is {formatDay(carrierDeadline)}. If you continue with{" "}
                      {carrierName}, it is very likely that your coverage start will be pushed out to {formatDay(nextCarrierDeadline)}.
                    </p>
                    <br />
                    <p>
                      If you absolutely need coverage to start on {formatDay(selectedCoverageStartDate)}, you may want to choose a plan from another carrier
                      that has a later deadline.
                    </p>
                    <p>Carrier deadlines for {formatDay(selectedCoverageStartDate)} coverage start:</p>
                    <ul>
                      {alternativeCarriers.map((carrier) => (
                        <li key={carrier.brand_name}>
                          {carrier.brand_name} - {formatDay(parse(carrier.deadline, "yyyy-MM-dd", new Date()))}
                        </li>
                      ))}
                    </ul>
                    <br />
                    <Button color="gray" size="small" onClick={goToQuotePage}>
                      Choose a Different Plan
                    </Button>
                  </>
                }
              />
            </AppearingElement>
            <AppearingElement show={carrierDeadlineKnown && !carrierDeadlinePassed}>
              <Callout className="mx-4" type="info" body={`Please be sure to complete all application steps by ${formatDay(carrierDeadline)}.`} />
            </AppearingElement>
          </>
        )}
      </Row>
    </Div>
  );
};

export default CoverageStartDateSection;
